import React, { useEffect, useRef, useState } from 'react';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import Popover from '@material-ui/core/Popover';
import Button from '@material-ui/core/Button';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import FormGroup from '@material-ui/core/FormGroup';
import Checkbox from '@material-ui/core/Checkbox';
import Grid from '@material-ui/core/Grid';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import Collapse from '@material-ui/core/Collapse';
import Box from '@material-ui/core/Box';
import Slider from '@material-ui/core/Slider';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import { useAuth } from "../../context/auth";

const MILLS_PRODUCT_CATEGORIES = {
  G: 'Finished gasoline',
  R: 'RFG blendstock',
  C: 'Conventional blendstock',
  U: 'Unblended (ethanol-free) gasoline',
  D: 'Diesel/distillate',
  E: 'Ethanol',
};


const useStyles = makeStyles((theme) => ({
  control_spacing: {
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(1.5),
  },
  popoverPaper: {
    maxHeight: 'calc(100vh - 32px)',
    overflowY: 'auto',
  },
  div: {
    paddingTop: 20,
    display: 'flex',
    alignItems: 'center',
  },
  subCheckbox: {
    marginLeft: theme.spacing(4),
  },
  subLabels: {
    fontSize: '.85rem',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    minWidth: 300,
  },
}));

const ColoredLine = ({ color }) => (
  <span
    style={{
      display: 'inline-block',
      width: '20px',
      height: '2px',
      backgroundColor: color,
      marginLeft: '8px',
      verticalAlign: 'middle',
    }}
  />
);

const ColoredDot = ({ color }) => (
  <span
    style={{
      display: 'inline-block',
      width: '8px',
      height: '8px',
      backgroundColor: color,
      marginLeft: '8px',
      border: '1px solid #B9B4B3',
      verticalAlign: 'middle',
      borderRadius: '50%',
    }}
  />
);

const StyledTooltip = withStyles({
  tooltip: {
    fontSize: '0.7rem',
    padding: '4px 6px',
  },
})(Tooltip);

function ValueLabelComponent(props) {
  const { children, open, value } = props;
  return (
    <StyledTooltip open={open} enterTouchDelay={0} placement="top" title={`${value}`}>
      {children}
    </StyledTooltip>
  );
}

export default function OverlayMenu({
  overlay,
  setOverlay,
  subOverlay,
  setSubOverlay,
  csvPipelineOptions,
  crudePipelineOptions,
  crudeFilters,
  setCrudeFilters,
  refinedPipelineOptions,
  refinedFilters,
  setRefinedFilters,
  additionalTerminalFilters,
  setAdditonalTerminalFilters,
  sourceOptions,
  rackPricingFilters,
  setRackPricingFilters,
  showOverlayTable,
  setShowOverlayTable,
  onShowOverlayTable,
  millsOnly = false,
}) {
  const [open, setOpen] = useState(false);
  const {extendedOverlayAccess} = useAuth()
  const canViewExtendedOverlays = Number(extendedOverlayAccess) === 1;
  const classes = useStyles();
  const popoverActionRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [intermodalExpanded, setIntermodalExpanded] = useState(false);
  const [detailExpanded, setDetailExpanded] = useState(false);
  const [crudePipelineExpanded, setCrudePipelineExpanded] = useState(false);
  const [refinedPipelineExpanded, setRefinedPipelineExpanded] = useState(false);
  const [additionalTerminalsExpanded, setAdditionalTerminalsExpanded] = useState(false);

  const [capacitySlider, setCapacitySlider] = useState([0, subOverlay.maxCapacity]);
  const [detailCapacitySlider, setDetailCapacitySlider] = useState([0, subOverlay.detail_maxCapacity]);

  const openMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  const handleTableClick = () => {
    if (showOverlayTable) {
      setShowOverlayTable(false);
      return;
    }

    setShowOverlayTable(true);
    handleClose();
    if (onShowOverlayTable) {
      onShowOverlayTable();
    }
  };

  const handleChange = (event) => {
    const { name } = event.target;
    setOverlay((prev) => ({ ...prev, [name]: !prev[name] }));

    if (name === 'intermodal') setIntermodalExpanded((prev) => !prev);
    if (name === 'product_detail') setDetailExpanded((prev) => !prev);
    if (name === 'crude') setCrudePipelineExpanded((prev) => !prev);
    if (name === 'refined') setRefinedPipelineExpanded((prev) => !prev);
    if (name === 'additional_terminals') setAdditionalTerminalsExpanded((prev) => !prev);
  };

  const handleSubChange = (event) => {
    const { name } = event.target;
    setSubOverlay((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleRackCategoryChange = (categoryCode) => {
    setRackPricingFilters((previous) => {
      const categories = previous.categories || [];
      return {
        ...previous,
        categories: categories.includes(categoryCode)
          ? categories.filter((code) => code !== categoryCode)
          : [...categories, categoryCode],
      };
    });
  };

  const handleSliderChange = (event, newValue) => setCapacitySlider(newValue);
  const handleDetailSliderChange = (event, newValue) => setDetailCapacitySlider(newValue);
  const changeCapacity = (event, newValue) => setSubOverlay((prev) => ({ ...prev, capacity: newValue }));
  const changeDetailCapacity = (event, newValue) =>
    setSubOverlay((prev) => ({ ...prev, detail_storage: newValue }));

  useEffect(() => {
    if (subOverlay.maxCapacity)
      setCapacitySlider((prev) => [prev[0] || 0, subOverlay.maxCapacity]);
  }, [subOverlay.maxCapacity]);

  useEffect(() => {
    if (subOverlay.detail_maxCapacity)
      setDetailCapacitySlider([0, subOverlay.detail_maxCapacity]);
  }, [subOverlay.detail_maxCapacity]);

  useEffect(() => {
    if (!open || !anchorEl) return undefined;

    const scrollContainer = anchorEl.closest('main');
    if (!scrollContainer) return undefined;

    const scrollPage = (event) => {
      if (event.target.closest?.('[data-overlay-menu="true"]')) return;

      event.preventDefault();
      event.stopPropagation();
      scrollContainer.scrollTop += event.deltaY;
      window.requestAnimationFrame(() => popoverActionRef.current?.updatePosition());
    };

    document.addEventListener('wheel', scrollPage, { capture: true, passive: false });
    return () => document.removeEventListener('wheel', scrollPage, { capture: true });
  }, [anchorEl, open]);

  const convertBarrelsToMbbl = (barrels) => Number(barrels) / 1_000_000;

  return (
    <div className={classes.div}>
      <Button variant="contained" color="primary" onClick={openMenu} endIcon={<ArrowDropDownIcon />}>
        Overlays
      </Button>
      <Popover
        action={popoverActionRef}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        disableScrollLock
        PaperProps={{
          className: classes.popoverPaper,
          'data-overlay-menu': 'true',
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Grid>
          <Grid item className={classes.control_spacing}>
            <FormControl>
              <Box className={classes.headerRow}>
                <FormLabel>Overlays</FormLabel>
                <Button
                  color="primary"
                  size="small"
                  variant={showOverlayTable ? 'contained' : 'outlined'}
                  onClick={handleTableClick}
                >
                  {showOverlayTable ? 'Hide Table' : 'View Table'}
                </Button>
              </Box>
              <FormGroup>
                {!millsOnly && (
                  <>
                {/* Crude Pipelines */}
                <FormControlLabel
                  name="crude"
                  control={<Checkbox color="primary" checked={overlay.crude} size="small" />}
                  label={
                    <>
                      Crude Oil Pipelines
                      <ColoredLine color="#8B0000" />
                    </>
                  }
                  onChange={handleChange}
                />
                <Collapse in={crudePipelineExpanded}>
                  <Box mt={1}>
                    <Autocomplete
                      multiple
                      options={crudePipelineOptions}
                      value={crudeFilters.crude_pipelines || []}
                      onChange={(e, value) =>
                        setCrudeFilters((prev) => ({ ...prev, crude_pipelines: value }))
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Pipeline" variant="outlined" size="small" />
                      )}
                      style={{ minWidth: 250, maxWidth: '100%' }}
                      limitTags={2}
                      fullWidth
                    />
                  </Box>
                </Collapse>

                {/* Refined Pipelines */}
                <FormControlLabel
                  name="refined"
                  control={<Checkbox color="primary" checked={overlay.refined} size="small" />}
                  label={
                    <>
                      Petroleum Product Pipelines
                      <ColoredLine color="#00008B" />
                    </>
                  }
                  onChange={handleChange}
                />
                <Collapse in={refinedPipelineExpanded}>
                  <Box mt={1}>
                    <Autocomplete
                      multiple
                      options={refinedPipelineOptions}
                      value={refinedFilters.refined_pipelines || []}
                      onChange={(e, value) =>
                        setRefinedFilters((prev) => ({ ...prev, refined_pipelines: value }))
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Pipeline" variant="outlined" size="small" />
                      )}
                      style={{ minWidth: 250, maxWidth: '100%' }}
                      limitTags={2}
                      fullWidth
                    />
                  </Box>
                </Collapse>

                {/* Standard Overlays */}
                <FormControlLabel
                  name="padd"
                  control={<Checkbox color="primary" checked={overlay.padd} size="small" />}
                  label={
                    <>
                      Petroleum Administration for Defense Districts (PADD)
                      <ColoredLine color="#B9B4B3" />
                    </>
                  }
                  onChange={handleChange}
                />
                <FormControlLabel
                  name="product"
                  control={<Checkbox color="primary" checked={overlay.product} size="small" />}
                  label={
                    <>
                      Petroleum Product Terminals
                      <ColoredDot color="#32CD32" />
                    </>
                  }
                  onChange={handleChange}
                />

                {/* Product Detail */}
                {canViewExtendedOverlays && 
                  <>
                    <FormControlLabel
                  name="product_detail"
                  control={<Checkbox color="primary" checked={overlay.product_detail} size="small" />}
                  label={
                    <>
                      Product Terminals Detail
                      <ColoredDot color="#1E90FF" />
                    </>
                  }
                  onChange={handleChange}
                />
                <Collapse in={detailExpanded}>
                  <Box className={classes.subCheckbox}>
                    <FormLabel>Access Modes</FormLabel>
                    <FormGroup row>
                      {[
                        'detail_truck_in',
                        'detail_truck_out',
                        'detail_rail_in',
                        'detail_rail_out',
                        'detail_marine_in',
                        'detail_marine_out',
                      ].map((name) => (
                        <FormControlLabel
                          key={name}
                          name={name}
                          control={<Checkbox color="primary" checked={subOverlay[name]} size="small" />}
                          label={<span className={classes.subLabels}>{name.split('_')[1]} {name.split('_')[2]}</span>}
                          onChange={handleSubChange}
                        />
                      ))}
                    </FormGroup>

                    <FormLabel>Pipelines</FormLabel>
                    <FormGroup row>
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          multiple
                          options={csvPipelineOptions}
                          value={subOverlay.detail_pipeline_in || []}
                          onChange={(e, value) =>
                            setSubOverlay((prev) => ({
                              ...prev,
                              detail_pipeline_in: value.map((v) => v.toLowerCase()),
                            }))
                          }
                          renderInput={(params) => (
                            <TextField {...params} label="Pipeline In" variant="outlined" size="small" />
                          )}
                          style={{ minWidth: 250 }}
                          limitTags={2}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          multiple
                          options={csvPipelineOptions}
                          value={subOverlay.detail_pipeline_out || []}
                          onChange={(e, value) =>
                            setSubOverlay((prev) => ({
                              ...prev,
                              detail_pipeline_out: value.map((v) => v.toLowerCase()),
                            }))
                          }
                          renderInput={(params) => (
                            <TextField {...params} label="Pipeline Out" variant="outlined" size="small" />
                          )}
                          style={{ minWidth: 250 }}
                          limitTags={2}
                          fullWidth
                        />
                      </Grid>
                    </FormGroup>

                    <FormLabel>Products</FormLabel>
                    <FormGroup row>
                      {[
                        ['detail_asphalt', 'Asphalt'],
                        ['detail_chemicals', 'Chemicals'],
                        ['detail_lng', 'LNGs'],
                        ['detail_propane', 'Propane'],
                        ['detail_butane', 'Butane'],
                        ['detail_refined_products', 'Refined Products'],
                        ['detail_ethanol', 'Ethanol'],
                        ['detail_biodeisel', 'Biodiesel/Renewables'],
                        ['detail_crude', 'Crude Oil'],
                      ].map(([name, label]) => (
                        <FormControlLabel
                          key={name}
                          name={name}
                          control={<Checkbox color="primary" checked={subOverlay[name]} size="small" />}
                          label={<span className={classes.subLabels}>{label}</span>}
                          onChange={handleSubChange}
                        />
                      ))}
                    </FormGroup>

                    <FormLabel>Capacity</FormLabel>
                    <FormGroup>
                      <Slider
                        ValueLabelComponent={ValueLabelComponent}
                        value={detailCapacitySlider}
                        onChange={handleDetailSliderChange}
                        onChangeCommitted={changeDetailCapacity}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `${convertBarrelsToMbbl(v).toFixed(2)} MBBL`}
                        max={subOverlay.detail_maxCapacity}
                      />
                    </FormGroup>
                  </Box>
                </Collapse>
                  </>
                }
                

                {/* Intermodal */}
                <FormControlLabel
                  name="intermodal"
                  control={<Checkbox color="primary" checked={overlay.intermodal} size="small" />}
                  label={
                    <>
                      Intermodal Freight Facilities Pipeline Terminals
                      <ColoredDot color="#FFA500" />
                    </>
                  }
                  onChange={handleChange}
                />
                <Collapse in={intermodalExpanded}>
                  <Box className={classes.subCheckbox}>
                    <FormLabel>Access Modes</FormLabel>
                    <FormGroup row>
                      {['truck', 'rail', 'water'].map((mode) => (
                        <FormControlLabel
                          key={mode}
                          name={mode}
                          control={<Checkbox color="primary" checked={subOverlay[mode]} size="small" />}
                          label={<span className={classes.subLabels}>{mode.charAt(0).toUpperCase() + mode.slice(1)}</span>}
                          onChange={handleSubChange}
                        />
                      ))}
                    </FormGroup>

                    <FormLabel>Products</FormLabel>
                    <FormGroup row>
                      {[
                        ['crude', 'Crude'],
                        ['refined', 'Refined'],
                        ['pet_chem', 'Chemicals'],
                        ['ngl', 'NGL'],
                        ['gasoline', 'Gasoline'],
                        ['distillate', 'Distillate'],
                        ['biodiesel', 'Biodiesel'],
                        ['jetfuel', 'Jet Fuel'],
                        ['ethanol', 'Ethanol'],
                      ].map(([name, label]) => (
                        <FormControlLabel
                          key={name}
                          name={name}
                          control={<Checkbox color="primary" checked={subOverlay[name]} size="small" />}
                          label={<span className={classes.subLabels}>{label}</span>}
                          onChange={handleSubChange}
                        />
                      ))}
                    </FormGroup>

                    <FormLabel>Capacity</FormLabel>
                    <FormGroup>
                      <Slider
                        ValueLabelComponent={ValueLabelComponent}
                        value={capacitySlider}
                        onChange={handleSliderChange}
                        onChangeCommitted={changeCapacity}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `${convertBarrelsToMbbl(v).toFixed(2)} MBBL`}
                        max={subOverlay.maxCapacity}
                      />
                    </FormGroup>
                  </Box>
                </Collapse>

                {/* Rail Networks */}
                <FormControlLabel
                  name="bnsf"
                  control={<Checkbox color="primary" checked={overlay.bnsf} size="small" />}
                  label={
                    <>
                      North American Rail Network Lines - BNSF
                      <ColoredLine color="#2F4F4F" />
                    </>
                  }
                  onChange={handleChange}
                />
                <FormControlLabel
                  name="csx"
                  control={<Checkbox color="primary" checked={overlay.csx} size="small" />}
                  label={
                    <>
                      North American Rail Network Lines - CSX
                      <ColoredLine color="#006400" />
                    </>
                  }
                  onChange={handleChange}
                />
                <FormControlLabel
                  name="up"
                  control={<Checkbox color="primary" checked={overlay.up} size="small" />}
                  label={
                    <>
                      North American Rail Network Lines - UP
                      <ColoredLine color="#000000" />
                    </>
                  }
                  onChange={handleChange}
                />
                <FormControlLabel
                  name="ns"
                  control={<Checkbox color="primary" checked={overlay.ns} size="small" />}
                  label={
                    <>
                      North American Rail Network Lines - NS
                      <ColoredLine color="#8B4513" />
                    </>
                  }
                  onChange={handleChange}
                />

                {/* Marine Highways */}
                <FormControlLabel
                  name="marine"
                  control={<Checkbox color="primary" checked={overlay.marine} size="small" />}
                  label={
                    <>
                      Marine Highways
                      <ColoredLine color="#456898" />
                    </>
                  }
                  onChange={handleChange}
                />

                {/* Additional Terminals */}
                <FormControlLabel
                  name="additional_terminals"
                  control={<Checkbox color="primary" checked={overlay.additional_terminals} size="small" />}
                  label={
                    <>
                      Additional Terminals
                      <ColoredDot color="#ff1900ff" />
                    </>
                  }
                  onChange={handleChange}
                />

                <Collapse in={additionalTerminalsExpanded}>
                  <Box mt={1}>
                    <Autocomplete
                      multiple
                      options={sourceOptions}
                      value={additionalTerminalFilters.source || []}
                      onChange={(e, value) =>
                        setAdditonalTerminalFilters((prev) => ({ ...prev, source: value }))
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Source" variant="outlined" size="small" />
                      )}
                      style={{ minWidth: 250 }}
                      limitTags={2}
                      fullWidth
                    />
                  </Box>
                </Collapse>
                  </>
                )}

                {/* Mills MARC rack pricing */}
                <FormControlLabel
                  name="rack_pricing"
                  control={<Checkbox color="primary" checked={overlay.rack_pricing} size="small" />}
                  label={
                    <>
                      Mills Rack Price
                      <ColoredDot color="#f5a623" />
                    </>
                  }
                  onChange={handleChange}
                />
                <Collapse in={Boolean(overlay.rack_pricing)}>
                  <Box mt={1} ml={4} pb={3} style={{ display: 'grid', gap: 12 }}>
                    <Box>
                      <FormLabel>Product Categories</FormLabel>
                      <FormGroup row>
                        {Object.entries(MILLS_PRODUCT_CATEGORIES).map(([code, label]) => (
                          <FormControlLabel
                            key={code}
                            control={(
                              <Checkbox
                                color="primary"
                                size="small"
                                checked={(rackPricingFilters.categories || []).includes(code)}
                                onChange={() => handleRackCategoryChange(code)}
                              />
                            )}
                            label={<span className={classes.subLabels}>{label}</span>}
                          />
                        ))}
                      </FormGroup>
                    </Box>
                  </Box>
                </Collapse>
              </FormGroup>
            </FormControl>
          </Grid>
        </Grid>
      </Popover>
    </div>
  );
}
